// components/invoices/InvoiceModal.tsx
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ar, enGB, fr } from 'date-fns/locale';
import {
    FileText, X, Plus, Minus, Download, AlertTriangle,
    CheckCircle, DollarSign, Shield, Building
} from 'lucide-react';
import { Inspection } from '../../types/inspection';
import { invoiceService } from '../../services/invoiceService';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../../hooks/useLanguage';

interface InvoiceItem {
    reference: string;
    description: string;
    descriptionArabic?: string;
    descriptionEnglish?: string;
    descriptionFrench?: string;
    quantity: number;
    unitPriceHT: number;
    vatRate: number;
    discountPercent: number;
    isAddedAfterQuote: boolean;
}

interface ItemTotals {
    subtotalHT: number;
    vatAmount: number;
    subtotalTTC: number;
    discountAmount: number;
    finalPrice: number;
}

interface InvoiceModalProps {
    inspection: Inspection;
    acceptedQuote: any;
    onClose: () => void;
    onInvoiceCreated: (invoiceId: string) => void;
}

interface GarageUser {
    _id: string;
    garageName: string;
    address: { street: string; zipCode: string; city: string; };
    garagePhone: string;
    preferredLanguage?: string;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
                                                              inspection, acceptedQuote, onClose, onInvoiceCreated
                                                          }) => {
    const { t, isRTL, currentLanguage } = useLanguage();
    const [user] = useState<GarageUser>(JSON.parse(localStorage.getItem('user') || '{}'));

    const effectiveLanguage = user.preferredLanguage || currentLanguage;
    const effectiveIsRTL = effectiveLanguage === 'ar';

    const [items, setItems] = useState<InvoiceItem[]>([]);
    const [notes, setNotes] = useState('');
    const [notesArabic, setNotesArabic] = useState('');
    const [notesEnglish, setNotesEnglish] = useState('');
    const [notesFrench, setNotesFrench] = useState('');
    const [garageNotes, setGarageNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingQuoteDetails, setLoadingQuoteDetails] = useState(true);

    const getMessage = (ar: string, en: string, fr: string) => {
        switch (effectiveLanguage) {
            case 'ar': return ar;
            case 'fr': return fr;
            default: return en;
        }
    };

    useEffect(() => {
        const fetchQuoteDetails = async () => {
            if (!acceptedQuote) {
                toast.error(getMessage('خطأ: لا يوجد عرض سعر مقبول', 'Error: No accepted quote found', 'Erreur : Aucun devis accepté trouvé'));
                onClose();
                return;
            }
            try {
                setLoadingQuoteDetails(true);
                const { quoteService } = await import('../../services/quotesService');
                const quoteDetails = await quoteService.getQuoteDetails(acceptedQuote.quoteId || acceptedQuote._id);

                if (!quoteDetails.items || quoteDetails.items.length === 0) {
                    const totalHT = acceptedQuote.totalTTC ? acceptedQuote.totalTTC / 1.15 : 0;
                    const defaultDescription = getMessage('خدمة الصيانة والإصلاح', 'Maintenance and repair service', 'Service de maintenance et de réparation');
                    setItems([{ reference: 'SERVICE-001', description: defaultDescription, descriptionArabic: 'خدمة الصيانة والإصلاح', descriptionEnglish: 'Maintenance and repair service', descriptionFrench: 'Service de maintenance et de réparation', quantity: 1, unitPriceHT: totalHT, vatRate: 15, discountPercent: 0, isAddedAfterQuote: false }]);
                } else {
                    const mappedItems = quoteDetails.items.map((item: any, index: number) => ({
                        reference: item.reference || `ITEM-${index + 1}`,
                        description: item.description || getMessage('خدمة', 'Service', 'Service'),
                        descriptionArabic: item.descriptionArabic || item.description || 'خدمة',
                        descriptionEnglish: item.descriptionEnglish || item.description || 'Service',
                        descriptionFrench: item.descriptionFrench || item.description || 'Service',
                        quantity: item.quantity ?? 1,
                        unitPriceHT: item.unitPriceHT ?? 0,
                        vatRate: item.tvaRate ?? 15,
                        discountPercent: item.discountPercent ?? 0,
                        isAddedAfterQuote: false
                    }));
                    setItems(mappedItems);
                }
            } catch (error) {
                console.error('❌ Error fetching quote details:', error);
                toast.error(getMessage('خطأ في تحميل تفاصيل الفاتورة', 'Error loading quote details', 'Erreur lors du chargement des détails de la facture'));
                const totalHT = acceptedQuote.totalTTC ? acceptedQuote.totalTTC / 1.15 : 0;
                const defaultDescription = getMessage('خدمة الصيانة والإصلاح', 'Maintenance and repair service', 'Service de maintenance et de réparation');
                setItems([{ reference: 'SERVICE-001', description: defaultDescription, descriptionArabic: 'خدمة الصيانة والإصلاح', descriptionEnglish: 'Maintenance and repair service', descriptionFrench: 'Service de maintenance et de réparation', quantity: 1, unitPriceHT: totalHT, vatRate: 15, discountPercent: 0, isAddedAfterQuote: false }]);
            } finally {
                setLoadingQuoteDetails(false);
            }
        };
        fetchQuoteDetails();
    }, [acceptedQuote, effectiveLanguage, onClose]);

    const calculateItemTotals = (item: InvoiceItem): ItemTotals => {
        const subtotalHT = item.quantity * item.unitPriceHT;
        const vatAmount = subtotalHT * (item.vatRate / 100);
        const subtotalTTC = subtotalHT + vatAmount;
        const discountAmount = subtotalTTC * (item.discountPercent / 100);
        const finalPrice = subtotalTTC - discountAmount;
        return { subtotalHT, vatAmount, subtotalTTC, discountAmount, finalPrice };
    };

    const calculateTotals = () => {
        const quoteAmount = acceptedQuote?.totalTTC || 0;
        const totals = items.reduce((acc, item) => {
            const it = calculateItemTotals(item);
            return { totalHT: acc.totalHT + item.quantity * item.unitPriceHT, totalVAT: acc.totalVAT + it.vatAmount, totalDiscount: acc.totalDiscount + it.discountAmount, totalTTC: acc.totalTTC + it.finalPrice };
        }, { totalHT: 0, totalVAT: 0, totalDiscount: 0, totalTTC: 0 });
        return { ...totals, quoteAmount, additionalAmount: totals.totalTTC - quoteAmount };
    };

    const addSurpriseItem = () => {
        const ref = `ADD-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        const defaultDescription = getMessage('عمل إضافي', 'Additional work', 'Travaux supplémentaires');
        setItems([...items, { reference: ref, description: defaultDescription, descriptionArabic: 'عمل إضافي', descriptionEnglish: 'Additional work', descriptionFrench: 'Travaux supplémentaires', quantity: 1, unitPriceHT: 0, vatRate: 15, discountPercent: 0, isAddedAfterQuote: true }]);
    };

    const updateItem = (index: number, field: keyof InvoiceItem, value: number | string) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: typeof value === 'string' ? value : Number(value) };
        setItems(newItems);
    };

    const removeItem = (index: number) => {
        if (items.length === 1) { toast.error(getMessage('يجب أن يكون هناك عنصر واحد على الأقل', 'At least one item is required', 'Au moins un article est requis')); return; }
        setItems(items.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        const emptyDescriptions = items.filter(item => !item.description || item.description.trim() === '');
        if (emptyDescriptions.length > 0) { toast.error(getMessage('❌ يرجى ملء جميع الأوصاف', '❌ Please fill in all descriptions', '❌ Veuillez remplir toutes les descriptions')); return; }
        const zeroPrices = items.filter(item => item.unitPriceHT <= 0);
        if (zeroPrices.length > 0) { toast.error(getMessage('❌ يرجى إدخال أسعار صحيحة', '❌ Please enter valid prices', '❌ Veuillez entrer des prix valides')); return; }

        try {
            setLoading(true);
            const totals = calculateTotals();
            const currentDate = new Date();
            const invoiceData = {
                invoiceNumber: `INV-${currentDate.getTime()}`,
                inspectionId: inspection._id,
                quoteId: acceptedQuote._id || acceptedQuote.quoteId,
                garageId: user._id,
                status: 'finalized',
                items: items.map(item => { const it = calculateItemTotals(item); return { ...item, reference: item.reference.trim(), description: item.description.trim(), totalHT: item.quantity * item.unitPriceHT, totalTTC: it.finalPrice, vatAmount: it.vatAmount }; }),
                ...totals,
                notes, notesArabic, notesEnglish, notesFrench, garageNotes,
                currency: '',
                sellerInfo: { name: user.garageName, nameArabic: user.garageName, nameEnglish: user.garageName, nameFrench: user.garageName, vatNumber: effectiveLanguage === 'fr' ? 'FR00000000000' : '300000000000003', address: { street: user.address?.street || '', city: user.address?.city || '', postalCode: user.address?.zipCode || '', countryCode: effectiveLanguage === 'fr' ? 'FR' : 'SA' }, phoneNumber: user.garagePhone },
                customerInfo: { name: inspection.clientName, phoneNumber: inspection.clientPhone, address: inspection.location?.address || '' },
                paymentTerms: getMessage('الدفع نقداً', 'Cash payment', 'Paiement en espèces'),
                paymentTermsArabic: 'الدفع نقداً', paymentTermsEnglish: 'Cash payment', paymentTermsFrench: 'Paiement en espèces',
                paymentMethod: 'cash',
                completedAt: currentDate
            };
            const invoice = await invoiceService.createInvoice(invoiceData);
            toast.success(getMessage('✅ تم إنشاء الفاتورة بنجاح', '✅ Invoice created successfully', '✅ Facture créée avec succès'));
            onInvoiceCreated(invoice._id);
        } catch (error) {
            console.error('❌ Error creating invoice:', error);
            toast.error(getMessage('❌ خطأ في إنشاء الفاتورة', '❌ Error creating invoice', '❌ Erreur lors de la création de la facture'));
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number) => `${amount.toFixed(2)} `;
    const totals = calculateTotals();
    const hasAdditionalWork = totals.additionalAmount !== 0;

    // Guard states
    if (!acceptedQuote) return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex items-center gap-3 text-red-600 mb-4"><AlertTriangle className="w-6 h-6" /><h3 className="font-bold">{getMessage('خطأ في البيانات', 'Data Error', 'Erreur de données')}</h3></div>
                <p className="text-gray-600 mb-4 text-sm">{getMessage('لا يمكن إنشاء الفاتورة بدون عرض سعر مقبول صالح', 'Cannot create invoice without a valid accepted quote', 'Impossible de créer une facture sans un devis accepté valide')}</p>
                <button onClick={onClose} className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm">{getMessage('إغلاق', 'Close', 'Fermer')}</button>
            </div>
        </div>
    );

    if (loadingQuoteDetails) return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-8 max-w-md w-full">
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
                    <p className="text-gray-600 text-center text-sm">{getMessage('جاري تحميل تفاصيل الفاتورة...', 'Loading invoice details...', 'Chargement des détails de la facture...')}</p>
                </div>
            </div>
        </div>
    );

    if (items.length === 0) return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex items-center gap-3 text-red-600 mb-4"><AlertTriangle className="w-6 h-6" /><h3 className="font-bold">{getMessage('خطأ في البيانات', 'Data Error', 'Erreur de données')}</h3></div>
                <p className="text-gray-600 mb-4 text-sm">{getMessage('لا يمكن إنشاء الفاتورة بدون عناصر', 'Cannot create invoice without items', 'Impossible de créer une facture sans articles')}</p>
                <button onClick={onClose} className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm">{getMessage('إغلاق', 'Close', 'Fermer')}</button>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
            <div className={`bg-white w-full h-full overflow-y-auto sm:rounded-lg sm:h-auto sm:max-h-[90vh] sm:max-w-6xl sm:m-4 ${effectiveIsRTL ? 'rtl' : 'ltr'}`} dir={effectiveIsRTL ? 'rtl' : 'ltr'}>

                {/* Header */}
                <div className={`flex justify-between items-start p-4 sm:p-6 border-b sticky top-0 bg-white z-10 ${effectiveIsRTL ? 'flex-row-reverse' : ''}`}>
                    <div>
                        <div className="flex items-center gap-2 sm:gap-3 mb-1">
                            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                            <h2 className="text-base sm:text-2xl font-bold">{getMessage('فاتورة نهائية ', 'Final Invoice ', 'Facture finale ')}</h2>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600">{`Inspection #${inspection._id.substring(0, 8)}`}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0"><X className="w-5 h-5 sm:w-6 sm:h-6" /></button>
                </div>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    {/* Alert additional work */}
                    {hasAdditionalWork && totals.additionalAmount > 0 && (
                        <div className={`bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-start gap-2 ${effectiveIsRTL ? 'flex-row-reverse' : ''}`}>
                            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-medium text-yellow-800 text-sm">{getMessage('⚠️ تنبيه: أعمال إضافية', '⚠️ Additional Work Alert', '⚠️ Alerte : Travaux supplémentaires')}</h3>
                                <p className="text-xs text-yellow-700 mt-1">
                                    {getMessage(`المبلغ الإجمالي للفاتورة يختلف عن عرض السعر الأولي بمقدار +${formatCurrency(totals.additionalAmount)}`, `Invoice total differs from initial quote by +${formatCurrency(totals.additionalAmount)}`, `Le montant total de la facture diffère du devis initial de +${formatCurrency(totals.additionalAmount)}`)}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Vehicle & Client */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                            <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm sm:text-base">
                                <Building className="w-4 h-4" />
                                {getMessage('معلومات المركبة', 'Vehicle Information', 'Informations du véhicule')}
                            </h3>
                            <p className="text-sm"><span className="font-medium">{inspection.vehicleBrand} {inspection.vehicleModel}</span> ({inspection.vehicleYear})</p>
                        </div>
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                            <h3 className="font-semibold mb-2 text-sm sm:text-base">{getMessage('العميل', 'Client', 'Client')}</h3>
                            <p className="text-sm">{inspection.clientName}</p>
                            <p className="text-sm text-gray-600">{inspection.clientPhone}</p>
                        </div>
                    </div>

                    {/* Items table — horizontal scroll on mobile */}
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full px-4 sm:px-0">
                            <table className="w-full border border-gray-300 text-sm">
                                <thead className="bg-green-50 border-2 border-green-300">
                                <tr>
                                    {[
                                        getMessage('النوع', 'Type', 'Type'),
                                        getMessage('الوصف', 'Description', 'Description'),
                                        getMessage('الكمية', 'Qty', 'Qté'),
                                        getMessage('السعر', 'Price', 'Prix'),
                                        getMessage('ضريبة', 'VAT', 'TVA'),
                                        getMessage('المجموع', 'Total', 'Total'),
                                        ''
                                    ].map((label, i) => (
                                        <th key={i} className={`px-3 py-2 border font-semibold text-xs sm:text-sm ${i < 2 ? (effectiveIsRTL ? 'text-right' : 'text-left') : 'text-center'}`}>
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {items.map((item, index) => {
                                    const it = calculateItemTotals(item);
                                    return (
                                        <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <td className="px-3 py-2 border text-center whitespace-nowrap">
                                                {item.isAddedAfterQuote
                                                    ? <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">🔧 {getMessage('إضافي', 'Added', 'Ajouté')}</span>
                                                    : <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">✓ {getMessage('عرض', 'Quote', 'Devis')}</span>
                                                }
                                            </td>
                                            <td className="px-3 py-2 border min-w-[140px]">
                                                <input type="text" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} className={`w-full border rounded px-2 py-1 text-sm ${effectiveIsRTL ? 'text-right' : ''}`} placeholder={getMessage('الوصف...', 'Description...', 'Description...')} />
                                            </td>
                                            <td className="px-3 py-2 border text-center">
                                                <input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="w-14 border rounded px-1 py-1 text-center text-sm" min="1" />
                                            </td>
                                            <td className="px-3 py-2 border">
                                                <input type="number" value={item.unitPriceHT} onChange={(e) => updateItem(index, 'unitPriceHT', e.target.value)} className="w-20 border rounded px-1 py-1 text-center text-sm" step="0.01" min="0" />
                                            </td>
                                            <td className="px-3 py-2 border text-center text-sm">{item.vatRate}%</td>
                                            <td className="px-3 py-2 border text-center font-medium text-sm whitespace-nowrap">{formatCurrency(it.finalPrice)}</td>
                                            <td className="px-3 py-2 border text-center">
                                                {item.isAddedAfterQuote && (
                                                    <button onClick={() => removeItem(index)} className="text-red-600 hover:bg-red-50 p-1 rounded">
                                                        <Minus className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Add item */}
                    <button onClick={addSurpriseItem} className={`flex items-center text-orange-600 hover:text-orange-700 text-sm ${effectiveIsRTL ? 'flex-row-reverse' : ''}`}>
                        <Plus className={`w-5 h-5 ${effectiveIsRTL ? 'ml-1' : 'mr-1'}`} />
                        {getMessage('➕ إضافة عمل إضافي', ' Add additional work', 'Ajouter des travaux supplémentaires')}
                    </button>

                    {/* Totals */}
                    <div className="border-2 border-green-400 bg-green-50 p-4 sm:p-6 rounded-lg">
                        <div className="grid grid-cols-1 gap-2 sm:gap-3 max-w-md ml-auto text-sm sm:text-base">
                            <div className="flex justify-between">
                                <span className="text-gray-700">{getMessage('مبلغ عرض السعر الأولي:', 'Initial Quote Amount:', 'Montant du devis initial :')}</span>
                                <span className="font-medium">{formatCurrency(totals.quoteAmount)}</span>
                            </div>
                            {totals.additionalAmount > 0 && (
                                <div className="flex justify-between text-orange-600">
                                    <span>{getMessage('أعمال إضافية:', 'Additional Work:', 'Travaux supplémentaires :')}</span>
                                    <span className="font-medium">+{formatCurrency(totals.additionalAmount)}</span>
                                </div>
                            )}
                            <div className="border-t pt-2 flex justify-between">
                                <span className="text-gray-700">{getMessage('الإجمالي بدون ضريبة:', 'Total excl. VAT:', 'Total HT :')}</span>
                                <span className="font-medium">{formatCurrency(totals.totalHT)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-700">{getMessage('ضريبة القيمة المضافة (15%):', 'VAT (15%):', effectiveLanguage === 'fr' ? 'TVA (20%) :' : 'VAT (15%):')}</span>
                                <span className="font-medium">{formatCurrency(totals.totalVAT)}</span>
                            </div>
                            <div className="border-t-2 pt-2 sm:pt-3 flex justify-between items-baseline">
                                <span className="text-base sm:text-xl font-bold">{getMessage('المجموع النهائي:', 'FINAL TOTAL:', 'TOTAL TTC :')}</span>
                                <span className="text-xl sm:text-2xl font-bold text-green-600">{formatCurrency(totals.totalTTC)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="space-y-3 sm:space-y-4">
                        <div>
                            <label className="block font-medium mb-1 sm:mb-2 text-sm sm:text-base">{getMessage('ملاحظات للعميل:', 'Notes for client:', 'Notes pour le client :')}</label>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={getMessage('ملاحظات إضافية...', 'Additional notes...', 'Notes supplémentaires...')} className={`w-full p-3 sm:p-4 border rounded-lg h-16 sm:h-20 text-sm ${effectiveIsRTL ? 'text-right' : ''}`} dir={effectiveIsRTL ? 'rtl' : 'ltr'} />
                        </div>
                        <div>
                            <label className="block font-medium mb-1 sm:mb-2 text-sm sm:text-base">{getMessage('ملاحظات داخلية (غير مرئية للعميل):', 'Internal notes (not visible to client):', 'Notes internes (non visibles pour le client) :')}</label>
                            <textarea value={garageNotes} onChange={(e) => setGarageNotes(e.target.value)} placeholder={getMessage('ملاحظات داخلية...', 'Internal notes...', 'Notes internes...')} className={`w-full p-3 sm:p-4 border rounded-lg h-16 sm:h-20 text-sm ${effectiveIsRTL ? 'text-right' : ''}`} dir={effectiveIsRTL ? 'rtl' : 'ltr'} />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={`flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-4 ${effectiveIsRTL ? 'sm:flex-row-reverse' : ''}`}>
                        <button onClick={onClose} disabled={loading} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm order-last sm:order-first">
                            {getMessage('إلغاء', 'Cancel', 'Annuler')}
                        </button>
                        <button onClick={handleSubmit} disabled={loading} className="px-4 py-2 sm:px-6 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base">
                            {loading ? (
                                <><div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-t-2 border-b-2 border-white"></div><span>{getMessage('جاري الإنشاء...', 'Creating...', 'Création...')}</span></>
                            ) : (
                                <><CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /><span>{getMessage('✅ إنهاء وإنشاء الفاتورة', '✅ Finalize & Create Invoice', '✅ Finaliser & Créer la facture')}</span></>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};